import { IntentController } from './controller/IntentController';
import { GameState, buildDemoMap, type MapDefinition } from './model/gamestate/GameState';
import { createTileMapRenderer, type EntityContextActionEvent, type TileMapRenderer } from './renderer/tilemap/TileMapRenderer';
import { PlayerInventory } from './ui/PlayerInventory';
import { CodexWindow } from './ui/CodexWindow';
import { createCodexPicker } from './ui/CodexPicker';
import { DebugWindow } from './ui/DebugWindow';
import { ItemBar } from './ui/ItemBar';
import { SkillBar } from './ui/SkillBar';
import { PauseController } from './controller/PauseController';
import { InputController } from './controller/InputController';
import { CameraController } from './controller/CameraController';
import { EventHub } from './events/EventHub';
import { loadSpaceTimeConfig } from './spacetime/config';
import { SpaceTimeClient, type PlayerSnapshot } from './spacetime/SpaceTimeClient';
import { showLoginPrompt } from './ui/LoginPrompt';

import { getGrid } from './grid';
import { ChestUI } from './ui/ChestUI/index';

import { ActionLogger } from './utils/ActionLogger';
import { createStatusBar } from './ui/StatusBar';
import { createEntityIdCard } from './ui/EntityIdCard';
import { createActorInventoryWindow } from './ui/ActorInventoryWindow';
import { createSelfCharacterWindow } from './ui/SelfCharacterWindow';
import { createMinimap } from './ui/Minimap';
import { createKeyBindings } from './settings/KeyBindings';
import { createKeyBindingsPanel } from './ui/KeyBindingsWindow';
import { t } from './i18n';
import { runAuthFlow } from './app/bootstrap/auth';
import {
  getBootstrapSettings,
  persistRendererSetting,
  queryBootstrapDom,
  setBootstrapSettingsScope,
  setRendererSelectionVisibility,
  setupLocalization,
  setupRendererSelection
} from './app/bootstrap/dom';
import { createMapSubscriptionManager } from './app/subscriptions/map';
import { createSessionSubscriptionManager } from './app/subscriptions/session';
import { setupHotkeys } from './app/input/hotkeys';
import { createAdminMapEditController } from './app/admin/mapEditController';
import { createAdminPlayerHandlers, registerAdminMapHandlers, registerAdminMapObjectHandlers, registerAdminNpcHandlers } from './app/admin/handlers';
import { INPUT_INTENT_EVENT, type PlacementConfig } from './controller/InputController';
import { createAdminWindow } from './ui/admin';
import { setWindowPersistenceScope, windowRegistry } from './ui/windowing';
import {
  enterDungeonFromUserMenu as enterDungeonFromUserMenuLifecycle,
  loadActiveMap as loadActiveMapLifecycle,
  refreshNpcs as refreshNpcsLifecycle,
  unloadActiveMap as unloadActiveMapLifecycle,
} from './app/lifecycle/mapLifecycle';
import { createVisualAssetPlacementOffsetHotkeys } from './visualAssets/debugPlacementOffset';

const TILE_SIZE_PX = 73;

type RuntimeConfig = {
  username?: string;
  password?: string;
  playerId: string;
  instanceId: string;
};

type Cleanup = () => void;

function createMapHandlers(params: {
  client: SpaceTimeClient;
  config: RuntimeConfig;
  mapCache: Map<string, MapDefinition>;
  gameState: GameState;
  renderer: TileMapRenderer;
  statusBar: ReturnType<typeof createStatusBar> | null;
  hud: HTMLElement | null;
  playerInventory: PlayerInventory;
  inputControllerRef: () => InputController | null;
  mapSubscriptionManager: ReturnType<typeof createMapSubscriptionManager>;
  inventoryCapacityRef: { current: number };
}) {
  const unloadActiveMap = (): void => {
    unloadActiveMapLifecycle({
      stopMapSubscriptions: () => params.mapSubscriptionManager.stop(),
      gameState: params.gameState,
    });
  };

  const loadActiveMap = async (): Promise<void> => {
    await loadActiveMapLifecycle({
      client: params.client,
      config: params.config,
      mapCache: params.mapCache,
      gameState: params.gameState,
      renderer: params.renderer,
      statusBar: params.statusBar,
      hud: params.hud,
      getInventoryCapacity: () => params.inventoryCapacityRef.current,
      setInventoryCapacity: (next) => {
        params.inventoryCapacityRef.current = next;
      },
      startMapSubscriptions: () => params.mapSubscriptionManager.start(),
      onAfterMapLoaded: (map) => {
        params.inputControllerRef()?.setGrid(getGrid(map.gridType));
        params.playerInventory.setStorageAvailable(map.mapKind === 'town');
      },
    });
  };

  return { unloadActiveMap, loadActiveMap };
}

function setupPlayerInventoryEditing(playerInventory: PlayerInventory, skillBar: SkillBar): void {
  playerInventory.onOpen(() => {
    skillBar.setEditable(true);
  });

  playerInventory.onClose(() => {
    skillBar.setEditable(false);
  });
}

function setupChestInteractions(params: {
  eventHub: EventHub;
  gameState: GameState;
  chestUI: ChestUI;
  statusBar: ReturnType<typeof createStatusBar> | null;
}): Cleanup {
  const tryOpenChestUiAtPlayerPosition = (): void => {
    const pos = params.gameState.getCharacterPosition();
    const chestNpc = params.gameState
      .getNpcRuntime()
      .find((npc) => npc.npcType === 'chest' && npc.x === pos.x && npc.y === pos.y);
    if (chestNpc) {
      void params.chestUI.show(chestNpc);
    }
  };

  const handleInputIntent = (detail: { type?: string } | undefined): void => {
    if (detail?.type === 'interact') {
      tryOpenChestUiAtPlayerPosition();
    }
  };

  const handleGameStateChange = ((event: Event) => {
    const detail = (event as CustomEvent).detail as import('./model/gamestate/types').StateChangeDetail;
    if (detail.type === 'tickUpdated') {
      params.statusBar?.update({
        statusEffects: params.gameState.getActiveStatusEffects(),
      });
      return;
    }
    if (detail.type === 'chestInventoryUpdated') {
      if (detail.rows.length > 0) {
        const npcId = detail.rows[0].npcId;
        const npc = params.gameState.getNpcRuntime().find((row) => row.npcId === npcId);
        if (npc && params.chestUI.getCurrentNpcId() !== npcId) {
          void params.chestUI.show(npc);
        }
      } else {
        params.chestUI.hide();
      }
    }
  }) as EventListener;

  params.eventHub.on(INPUT_INTENT_EVENT, handleInputIntent);
  params.gameState.addEventListener('stateChanged', handleGameStateChange);

  return () => {
    params.eventHub.off(INPUT_INTENT_EVENT, handleInputIntent);
    params.gameState.removeEventListener('stateChanged', handleGameStateChange);
  };
}

function applyWindowPersistenceAfterLogin(config: RuntimeConfig): void {
  // Contract: set per-user scope first, then rehydrate existing registered windows.
  // Reversing this order can restore defaults under null scope and lose persisted UI state.
  setWindowPersistenceScope(config.username ?? config.playerId);
  windowRegistry.rehydrateAll();
}

async function bootstrap(): Promise<void> {
  const {
    stage,
    hud,
    overlay,
    unpauseButton,
    logoffButton,
    keyBindingsButton,
    inventoryOverlay,
    inventoryGrid,
    inventoryClose,
    codexOverlay,
    codexClose,
    debugOverlay,
    debugClose,
    itemBarSlots,
    skillBarSlots,
    statusBarRoot,
    statusEffectsRoot,
    languageSelect,
    rendererSelect,
    rendererRow,
  } = queryBootstrapDom();
  const bootstrapSettings = getBootstrapSettings();
  setupLocalization(languageSelect);
  setupRendererSelection(rendererSelect);
  document.documentElement.dataset.rendererMode = bootstrapSettings.renderer;

  const keyBindings = createKeyBindings();
  keyBindings.install();
  setWindowPersistenceScope(null);
  codexOverlay.hidden = true;

  if (hud) hud.textContent = t('hud.connecting');

  const eventHub = new EventHub();
  const directionalButtons = document.querySelectorAll<HTMLButtonElement>('[data-direction]');
  const config = loadSpaceTimeConfig();
  const spaceTimeClient = new SpaceTimeClient({ config });
  const mapCache = new Map<string, MapDefinition>();
  const itemBarUtils = new ItemBar(itemBarSlots, spaceTimeClient, eventHub, inventoryOverlay);
  const loginPrompt = showLoginPrompt();

  await runAuthFlow({
    loginPrompt,
    spaceTimeClient,
    keyBindings,
    config
  });
  setBootstrapSettingsScope(config.playerId);
  const scopedBootstrapSettings = getBootstrapSettings();
  document.documentElement.dataset.rendererMode = scopedBootstrapSettings.renderer;
  if (languageSelect) {
    languageSelect.value = scopedBootstrapSettings.language;
  }
  if (rendererSelect) {
    rendererSelect.value = scopedBootstrapSettings.renderer;
  }
  setRendererSelectionVisibility(rendererRow, rendererSelect, true);
  applyWindowPersistenceAfterLogin(config);
  const playerInventory = new PlayerInventory(spaceTimeClient, inventoryOverlay, inventoryGrid, inventoryClose);
  const codexWindow = new CodexWindow(codexOverlay, codexClose);
  const codexPicker = createCodexPicker();
  const debugWindow = new DebugWindow(debugOverlay, debugClose);

  const dummyMap = buildDemoMap();
  const gameState = new GameState(dummyMap, { x: 0, y: 0 }, 'loading');
  gameState.setSelfEntityId(config.playerId);
  const rendererMode =
    scopedBootstrapSettings.renderer === '2d'
      ? '2d'
      : scopedBootstrapSettings.renderer === 'world2d'
        ? 'world2d'
        : '3d';
  const projectionMode =
    scopedBootstrapSettings.renderer === '3d-orthographic' ? 'orthographic' : 'perspective';
  const renderer = await createTileMapRenderer(rendererMode, {
    container: stage,
    map: dummyMap,
    tileSize: TILE_SIZE_PX,
    gameState: gameState,
    eventHub,
    projectionMode,
  });
  const statusBar = statusBarRoot ? createStatusBar(
    statusBarRoot,
    gameState,
    statusEffectsRoot ?? undefined,
    (effectId: string) => {
      void spaceTimeClient.skills.cancelStatusEffect(effectId);
    }
  ) : null;
  const skillBarUtils = new SkillBar(skillBarSlots, spaceTimeClient, eventHub, gameState, codexPicker);

  if (hud) hud.textContent = t('hud.loadingWorld');

  const inventoryCapacityRef = { current: 10 };
  const USER_MENU_DUNGEONS: ReadonlyArray<{ id: string; name: string }> = [
    { id: 'test_dungeon_1', name: t('dungeons.list.testDungeon1') },
    { id: 'mixed_grid_dungeon', name: t('dungeons.list.mixedGridDungeon') },
  ];
  let mapSubscriptionManager: ReturnType<typeof createMapSubscriptionManager> | null = null;

  setupPlayerInventoryEditing(playerInventory, skillBarUtils);

  const handleMyPlayerUpdate = (thisPlayer: PlayerSnapshot | null): void => {
    if (!thisPlayer) return;
    statusBar?.update({
      hp: thisPlayer.hp,
      maxHp: thisPlayer.maxHp,
      mana: thisPlayer.mana,
      maxMana: thisPlayer.maxMana,
      stamina: thisPlayer.stamina,
      maxStamina: thisPlayer.maxStamina,
      statusEffects: gameState.getActiveStatusEffects(),
    });

    const currentMapId = gameState.getCurrentMapId();
    inventoryCapacityRef.current = thisPlayer.inventoryCapacity || inventoryCapacityRef.current;
    playerInventory.updateCapacity(inventoryCapacityRef.current);
    playerInventory.updateStorageCapacity(thisPlayer.storageCapacity);
    gameState.setMoveDistance(thisPlayer.moveDistance);
    gameState.setViewRadius(thisPlayer.viewRadius);

    gameState.updateServerQueue(thisPlayer.queue);

    if (
      thisPlayer.currentMapId === currentMapId &&
      thisPlayer.currentInstanceId === config.instanceId
    ) {
      gameState.applyAuthoritativeState(
        { x: thisPlayer.mapX, y: thisPlayer.mapY },
        thisPlayer.rotation
      );
    }

    if (
      thisPlayer.currentMapId !== currentMapId
      || thisPlayer.currentInstanceId !== config.instanceId
    ) {
      if (!renderer.isPortalTransitioning()) {
        gameState.startPortalTransition(thisPlayer.currentMapId);
        renderer.startPortalTransition(
          async () => {
            try {
              unloadActiveMap();
              await loadActiveMap();
              renderer.startPortalFadeIn();
            } catch (error) {
              console.error('Failed to complete portal transition', error);
              gameState.setTransitioning(false);
              renderer.cancelPortalTransition();
              if (hud) {
                hud.textContent = t('hud.errorLoadingMap');
              }
            }
          },
          () => { /* transition complete */ }
        );
      }
    }
  };

  const refreshNpcs = async (mapId: string, force = false): Promise<void> => refreshNpcsLifecycle({
    client: spaceTimeClient,
    gameState,
    mapId,
    force,
  });

  const entityIdCard = createEntityIdCard({ isAdmin: true });
  const actorInventoryWindow = createActorInventoryWindow();
  const selfCharacterWindow = createSelfCharacterWindow({
    getProfile: () => {
      const live = mapSubscriptionManager?.getPlayerInformation(config.playerId);
      const appearance = gameState.getSelfAppearance();
      return {
        actorId: live?.entityId || gameState.getSelfEntityId() || config.playerId,
        playerId: config.playerId,
        name: live?.displayName || config.playerDisplayName,
        guildName: live?.guildName,
        hp: live?.hp,
        maxHp: live?.maxHp,
        entityType: appearance.entityType || 'player',
        color: appearance.color || '#3b82f6',
        accessoriesJson: appearance.accessoriesJson || '{}',
      };
    },
  });
  let pauseController: PauseController | null = null;

  mapSubscriptionManager = createMapSubscriptionManager({
    client: spaceTimeClient,
    config,
    gameState,
    renderer,
    debugWindow,
    statusBar,
    entityIdCard,
    selfCharacterWindow,
    eventHub,
    onMyPlayerUpdate: handleMyPlayerUpdate,
  });

  const visualAssetPlacementOffsetHotkeys = createVisualAssetPlacementOffsetHotkeys(renderer);
  const cameraController = new CameraController(renderer);
  const minimap = createMinimap(gameState, renderer, cameraController, config.username);

  const sessionSubscriptionManager = createSessionSubscriptionManager({
    client: spaceTimeClient,
    config: { username: config.username ?? '' },
    gameState,
    renderer,
    cameraController,
    eventHub,
    hud,
    playerInventory,
    codexWindow,
    codexPicker,
    itemBarUtils,
    skillBarUtils,
    entityIdCard,
    dungeons: USER_MENU_DUNGEONS,
    onDungeonEnter: enterDungeonFromUserMenu,
  });

  renderer.addEventListener('entityContextAction', (event: Event) => {
    const detail = (event as EntityContextActionEvent).detail;
    if (detail.action === 'inventory') {
      if (!detail.profile) return;
      const profile = { ...detail.profile };
      void (async () => {
        const allRows = await spaceTimeClient.inventory.fetchVisibleActorInventory();
        const rows = allRows.filter((row) => row.actorId === profile.actorId);
        const itemCatalog = spaceTimeClient.catalogs.getCachedItemCatalog()
          ?? await spaceTimeClient.catalogs.syncItemCatalog();
        actorInventoryWindow.open(profile, rows, itemCatalog.entries);
      })();
      return;
    }
    if (detail.action === 'id') {
      if (!detail.profile) return;
      const profile = { ...detail.profile };
      if (profile.kind === 'remotePlayer' && profile.playerId) {
        const live = mapSubscriptionManager?.getPlayerInformation(profile.playerId);
        if (live) {
          profile.actorId = live.entityId || profile.actorId;
          profile.name = live.displayName || profile.name;
          profile.guildName = live.guildName;
          profile.hp = live.hp;
          profile.maxHp = live.maxHp;
          profile.rotation = live.rotation;
        }
      }
      entityIdCard.open(profile);
      return;
    }
    if (detail.action !== 'trade') return;
    if (!detail.targetName) return;
    void sessionSubscriptionManager.getUserMenuPopupHandle()?.openTradeWith(detail.targetName);
  });

  let inputControllerRef: InputController | null = null;
  const { unloadActiveMap, loadActiveMap } = createMapHandlers({
    client: spaceTimeClient,
    config,
    mapCache,
    gameState,
    renderer,
    statusBar,
    hud,
    playerInventory,
    inputControllerRef: () => inputControllerRef,
    mapSubscriptionManager: mapSubscriptionManager!,
    inventoryCapacityRef,
  });

  async function enterDungeonFromUserMenu(dungeonId: string): Promise<void> {
    await enterDungeonFromUserMenuLifecycle(
      {
        pauseController,
        client: spaceTimeClient,
        unloadActiveMap,
        loadActiveMap,
      },
      dungeonId
    );
  }

  await sessionSubscriptionManager.start();
  await spaceTimeClient.players.subscribeToMyStatusEffects({
    onUpdate: (effects) => {
      gameState.setStatusEffects(effects);
      statusBar?.update({
        statusEffects: effects,
      });
    }
  });
  await loadActiveMap();

  const inputController = new InputController({
    renderer,
    getReferencePosition: () => gameState.getQueueCursor(),
    getReferenceRotation: () => gameState.getQueueFrontRotation(),
    directionalButtons,
    eventHub
  });
  inputController.setGrid(getGrid(gameState.getMap().gridType));
  inputControllerRef = inputController;
  const playerSnapshot = await spaceTimeClient.players.fetchPlayerSnapshot(config.playerId);
  if (!playerSnapshot) {
    throw new Error(`Player ${config.playerId} not found`);
  }
  let isSuperAdmin = (await spaceTimeClient.admin.getMyAdminStatus())?.isSuperAdmin === true;
  const adminPlayerHandlers = createAdminPlayerHandlers({
    client: spaceTimeClient,
    gameState,
  });
  const adminMapEditController = createAdminMapEditController({
    client: spaceTimeClient,
    gameState,
    renderer,
    mapCache,
    refreshNpcs,
  });
  const applyAdminToolSelection = (selection: PlacementConfig | null): void => {
    inputController.setInteractionMode(selection ? 'placement' : 'movement');
    inputController.setPlacementConfig(selection);
  };
  const adminWindow = createAdminWindow({
    player: playerSnapshot,
    getIsSuperAdmin: () => isSuperAdmin,
    onToolSelect: applyAdminToolSelection,
    onWipeAndReloadMaps: () => adminMapEditController.wipeAndReloadMaps(),
    onUploadMapJson: () => adminMapEditController.uploadMapJson(),
    onSaveFrontendMapAsset: () => adminMapEditController.saveFrontendMapAsset(),
    onSetMapDefaultBiome: async ({ mapId, biome }) => {
      await adminMapEditController.setMapDefaultBiome(mapId, biome);
    },
    loadMapCatalog: async () => spaceTimeClient.admin.listMaps(),
    createMap: async ({ mapId, name, width, height, gridType }) => {
      await spaceTimeClient.admin.createMap(mapId, name, width, height, gridType);
      await spaceTimeClient.admin.createInstance(mapId, 'channel_1');
    },
    deleteMap: async (mapId) => {
      await spaceTimeClient.admin.deleteMap(mapId);
    },
    getCurrentMapId: () => gameState.getCurrentMapId(),
    getCurrentMapGridShape: () => {
      const gridType = gameState.getMap().gridType;
      return gridType === 'square' || gridType === 'triangle' || gridType === 'hex'
        ? gridType
        : 'hex';
    },
    scanPlayersOnMap: adminPlayerHandlers.scanPlayersOnMap,
    disconnectPlayerSession: adminPlayerHandlers.disconnectPlayerSession,
    bringPlayerHere: adminPlayerHandlers.bringPlayerHere,
  });
  await spaceTimeClient.admin.subscribeToMyAdminStatus({
    onUpdate: (status) => {
      const nextIsSuperAdmin = status?.isSuperAdmin === true;
      if (nextIsSuperAdmin === isSuperAdmin) {
        return;
      }
      isSuperAdmin = nextIsSuperAdmin;
      adminWindow.refresh();
    },
  });

  const chestUI = new ChestUI(spaceTimeClient, spaceTimeClient.inventory);
  const cleanupChestInteractions = setupChestInteractions({
    eventHub,
    gameState,
    chestUI,
    statusBar,
  });

  new IntentController(inputController, renderer, gameState, eventHub, {
    client: spaceTimeClient,
    characterId: config.playerId,
    instanceId: config.instanceId,
    getTickTarget: () => gameState.claimNextTickTarget()
  }, hud);

  const unregisterAdminNpcHandlers = registerAdminNpcHandlers({
    eventHub,
    client: spaceTimeClient,
    gameState,
    config: { playerId: config.playerId },
    refreshNpcs,
  });
  const unregisterAdminMapHandlers = registerAdminMapHandlers({
    eventHub,
    client: spaceTimeClient,
    gameState,
    renderer,
    controller: adminMapEditController,
  });
  const unregisterAdminMapObjectHandlers = registerAdminMapObjectHandlers({
    eventHub,
    client: spaceTimeClient,
    gameState,
    renderer,
    controller: adminMapEditController,
  });

  let keyBindingsPanelVisible = false;
  pauseController = new PauseController({ overlay, toggleButton: unpauseButton, eventHub });
  const keyBindingsPanel = createKeyBindingsPanel(keyBindings, {
    onShow: () => {
      keyBindingsPanelVisible = true;
    },
    onHide: () => {
      keyBindingsPanelVisible = false;
    },
  });
  document.body.appendChild(keyBindingsPanel.element);

  keyBindingsButton?.addEventListener('click', () => {
    keyBindingsPanel.show();
  });

  const handleLogoff = async (): Promise<void> => {
    if (logoffButton.disabled) return;
    logoffButton.disabled = true;
    try {
      await spaceTimeClient.disconnect();
      window.location.reload();
    } catch (error) {
      logoffButton.disabled = false;
      const message = error instanceof Error ? error.message : String(error);
      ActionLogger.failure('Log off', error);
      alert(t('app.logoffFailed', { error: message }));
    }
  };

  logoffButton.addEventListener('click', () => {
    void handleLogoff();
  });

  const hotkeys = setupHotkeys({
    pauseController,
    inputController,
    playerInventory,
    selfCharacterWindow,
    chatBox: sessionSubscriptionManager.getChatBoxHandle()!,
    minimap,
    codexWindow,
    debugWindow,
    adminWindow,
    inventoryOverlay,
    codexOverlay,
    debugOverlay,
    itemBarUtils,
    skillBarUtils,
    keyBindingsPanel,
    isKeyBindingsPanelVisible: () => keyBindingsPanelVisible,
    getUserMenuPopupHandle: () => sessionSubscriptionManager.getUserMenuPopupHandle(),
  });

  window.addEventListener('beforeunload', () => {
    const cleanups: Cleanup[] = [
      () => { void spaceTimeClient.disconnect(); },
      () => hotkeys.dispose(),
      cleanupChestInteractions,
      unregisterAdminNpcHandlers,
      unregisterAdminMapHandlers,
      unregisterAdminMapObjectHandlers,
      () => sessionSubscriptionManager.stop(),
      () => mapSubscriptionManager?.stop(),
      () => entityIdCard.destroy(),
      () => actorInventoryWindow.destroy(),
      () => selfCharacterWindow.destroy(),
      () => minimap.destroy(),
      () => cameraController.dispose(),
      () => visualAssetPlacementOffsetHotkeys.dispose(),
      cleanupHexPathStretchHotkeys,
      () => keyBindings.dispose(),
      () => statusBar?.destroy(),
      () => chestUI.destroy(),
      () => adminWindow.destroy(),
      () => debugWindow.destroy(),
      () => codexPicker.destroy(),
      () => codexWindow.destroy(),
      () => playerInventory.destroy(),
    ];
    cleanups.forEach((cleanup) => {
      cleanup();
    });
  });

  hotkeys.setContext('gameplay');
} // End bootstrap

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(console.error);
  });
}

bootstrap().catch(console.error);
